import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createConsumer, Consumer } from '@rails/actioncable';

// WebSocket URL (ActionCable uses /cable by convention)
const wsUrl = process.env.NODE_ENV === 'production'
  ? 'wss://api.padlo.co/socket'
  : 'ws://localhost:3000/socket/matches';

interface SocketContextValue {
  connected: boolean;
  subscribe: (channelName: string, params?: Record<string, any>, callbacks?: ChannelCallbacks) => () => void;
  consumer: Consumer | null;
}

interface ChannelCallbacks {
  received?: (data: any) => void;
  connected?: () => void;
  disconnected?: () => void;
}

interface MatchData {
  updated_at: string;
  [key: string]: any;
}

const SocketContext = createContext<SocketContextValue | null>(null);

/**
 * Provider component that manages ActionCable WebSocket connection
 * and provides subscription functionality for components
 */
export function SocketProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const [consumer, setConsumer] = useState<Consumer | null>(null);
  const queryClient = useQueryClient();

  // Initialize ActionCable consumer with JWT auth
  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (!token) return;

    // Create ActionCable consumer with headers for authentication
    const cableConsumer = createConsumer(`${wsUrl}?token=${token}`);

    setConsumer(cableConsumer);
    setConnected(true);

    return () => {
      cableConsumer.disconnect();
      setConnected(false);
    };
  }, []);

  // Default message handler to automatically invalidate React Query cache
  const defaultMessageHandler = (data: any) => {
    if (!data || !data.type) return;

    // Handle match_updated event from AuthenticatedMatchesChannel
    if (data.type === 'match_updated' && data.match_id && data.updated_at) {
      // Get the current match data from cache
      const matchQueryKey = ['match', data.match_id];
      const matchData = queryClient.getQueryData<MatchData>(matchQueryKey);
      // try to fetch the last ball updated at from matchData.points.b_updated_at
      const lastBallUpdatedAt = matchData?.points.b_updated_at;
      const matchUpdatedAt = matchData?.updated_at ? new Date(matchData.updated_at) : null;
      const ballUpdatedAt = lastBallUpdatedAt ? new Date(lastBallUpdatedAt) : null;
      const largestUpdated = Math.max(matchUpdatedAt?.getTime() || 0, ballUpdatedAt?.getTime() || 0);

      // Check if we have cached data and if it's older than the update
      if (largestUpdated && largestUpdated < new Date(data.updated_at).getTime()) {
        console.log(`Invalidating match ${data.match_id} - cache is older than update`);
        queryClient.invalidateQueries({ queryKey: matchQueryKey });
        queryClient.invalidateQueries({ queryKey: ['matches', data.match_id, 'balls'] });
      } else if (!matchData) {
        // If we don't have the match in cache, we don't need to invalidate
        console.log(`Match ${data.match_id} not in cache, skipping invalidation`);
      } else {
        console.log(`Match ${data.match_id} cache is up to date, skipping invalidation`);
      }
    }
  };

  // Subscribe to an ActionCable channel
  const subscribe = (
    channelName: string,
    params: Record<string, any> = {},
    callbacks: ChannelCallbacks = {}
  ) => {
    if (!consumer) {
      console.warn('Cannot subscribe: WebSocket not connected');
      return () => {};
    }

    // Create subscription with merged params
    const subscription = consumer.subscriptions.create(
      { channel: channelName, ...params },
      {
        connected: callbacks.connected,
        disconnected: callbacks.disconnected,
        received: (data) => {
          // Try to parse data as JSON if it's a string
          let parsedData = data;
          if (typeof data === 'string') {
            try {
              parsedData = JSON.parse(data);
            } catch (e) {
              console.warn('Failed to parse WebSocket message as JSON:', e);
            }
          }

          // Always run default handler for cache invalidation
          defaultMessageHandler(parsedData);

          // Then run custom handler if provided
          if (callbacks.received) {
            callbacks.received(parsedData);
          }
        }
      }
    );

    // Return unsubscribe function
    return () => {
      subscription.unsubscribe();
    };
  };

  return (
    <SocketContext.Provider value={{ connected, subscribe, consumer }}>
      {children}
    </SocketContext.Provider>
  );
}

// Hook to access the socket context
export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
